// lib/koi-pond-v3/animation/koiSwim.ts
//
// T8.3 — swimming animation as a GPU vertex deformation. The plan named
// a CPU "bone chain" (koiSkeleton); we implement the same motion as an
// analytic traveling sine wave in the vertex shader instead. It's
// cheaper (no per-frame CPU skinning, the 3 koi keep sharing one
// geometry buffer), and it composes with the koi material's existing
// vertex stage. The body's +X axis leads; the swim undulates laterally
// along +Z.
//
// HOW THE WATCH-ITEMS ARE SATISFIED
//   - Amplitude ramps from ~0 at the head to max out through the caudal
//     fin via the bodyFraction attribute (0 head → 1 peduncle → 1.6 tail
//     tip). pow(bf/1.6, 1.8) keeps the head almost still and concentrates
//     flex in the tail.
//   - The traveling term (uSwimFreq * bf) means the wave propagates head→
//     tail, so the tail trails the body with a natural phase lag — the
//     "whip". No separate phase-lag term needed.
//   - Fins (pectoral/pelvic/dorsal/anal) carry a SINGLE bodyFraction equal
//     to their attachment point, so the wave displaces every fin vertex by
//     the SAME amount: the fin translates rigidly with the body ring it's
//     glued to. It sways with the body but never snakes internally or
//     tears off. (Independent fin flutter is a deliberate later add — the
//     brief asks for "at most a gentle flutter", so rigid-attached is the
//     safe first pass.)
//
// VELOCITY HOOK
//   uSwimGain scales amplitude+beat together. T8.4 (path following) will
//   drive it from per-koi velocity so fast koi flex harder than drifters.
//   Until then it sits at 1.0.
//
// NORMALS
//   Pure lateral displacement would leave normals stale and the lighting
//   flat during motion. We rotate each normal around Y by the analytic
//   yaw of the centerline (atan of d(disp)/dx), so the moonlit diffuse +
//   fresnel rim track the bend. Standard "sine displacement + analytic
//   normal" technique.

export const SWIM_UNIFORM_DECL = `
  uniform float uSwimTime;
  uniform float uSwimAmp;    // lateral amplitude at the tail tip (world units)
  uniform float uSwimFreq;   // spatial frequency — wave count along the body
  uniform float uSwimSpeed;  // temporal frequency (rad/s) — tail-beat rate
  uniform float uSwimPhase;  // per-koi phase offset (breaks lockstep)
  uniform float uSwimGain;   // velocity gain hook (T8.4); 1.0 at rest
`;

// Requires attributes `bodyFraction` + the standard `position`/`normal`.
// Produces `vec3 swimPos` and `vec3 swimNrm` for the caller to use in
// place of position/normal. Drop into the vertex shader main() AFTER the
// varyings are assigned and BEFORE the world-space transform.
export const SWIM_VERTEX_SNIPPET = `
  float _bf = bodyFraction;
  // amplitude ramp: head ~0 → tail tip max (bf runs 0..1.6)
  float _ramp = pow(clamp(_bf / 1.6, 0.0, 1.0), 1.8);
  float _amp = uSwimAmp * uSwimGain * _ramp;
  float _phase = uSwimFreq * _bf - uSwimSpeed * uSwimTime + uSwimPhase;
  float _disp = _amp * sin(_phase);

  vec3 swimPos = position;
  swimPos.z += _disp;

  // Analytic yaw for the normal. Over the body, bodyFraction ≈ linear in
  // x (bf ≈ (0.5 - x) / 0.95), so dbf/dx ≈ -1.05. Ignore the slowly-
  // varying d(_amp)/dx term — the cos term dominates the local slope.
  float _dDispDx = _amp * cos(_phase) * uSwimFreq * (-1.05);
  float _yaw = atan(_dDispDx);
  float _cy = cos(_yaw);
  float _sy = sin(_yaw);
  vec3 swimNrm = normal;
  swimNrm.x = normal.x * _cy + normal.z * _sy;
  swimNrm.z = -normal.x * _sy + normal.z * _cy;
`;

export interface SwimParams {
  amp?: number;
  freq?: number;
  speed?: number;
  phase?: number;
  gain?: number;
}

// Default swim uniforms — a calm, contemplative pond beat. Returns the
// uniform objects so the caller can update uSwimTime each frame and tune
// per koi. Merge into a material's uniforms.
export function makeSwimUniforms(p: SwimParams = {}) {
  return {
    uSwimTime: { value: 0 },
    uSwimAmp: { value: p.amp ?? 0.13 },
    uSwimFreq: { value: p.freq ?? 2.5 },
    // tail-beat rate. Calm pond → a slow, unhurried beat (was 3.0, read
    // as too fast against the contemplative mood).
    uSwimSpeed: { value: p.speed ?? 1.8 },
    uSwimPhase: { value: p.phase ?? 0 },
    uSwimGain: { value: p.gain ?? 1.0 },
  };
}
